import type { Storage, StorageOptions, StorageType } from "./types";

export class WebStorage implements Storage {
  private namespace: string;
  private storageType: StorageType;

  constructor(options: StorageOptions & { storageType?: StorageType }) {
    this.namespace = options.namespace;
    this.storageType = options.storageType ?? "localStorage";
  }

  private getStorage(): globalThis.Storage | null {
    if (typeof window === "undefined") {
      console.warn(`WebStorage (${this.storageType}) is not available`);
      return null;
    }
    return this.storageType === "sessionStorage"
      ? window.sessionStorage
      : window.localStorage;
  }

  private getFullKey(key: string): string {
    return `${this.namespace}-${key}`;
  }

  set(key: string, value: string): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }
    storage.setItem(this.getFullKey(key), value);
    return Promise.resolve();
  }

  get(key: string): Promise<string | null> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve(null);
    }
    return Promise.resolve(storage?.getItem(this.getFullKey(key)) ?? null);
  }

  remove(key: string): Promise<void> {
    const storage = this.getStorage();
    if (!storage) {
      return Promise.resolve();
    }
    storage.removeItem(this.getFullKey(key));
    return Promise.resolve();
  }
}

export const createStorage = (options: StorageOptions): Storage => {
  return new WebStorage(options);
};

export const createSessionStorage = (
  options: StorageOptions & { storageType?: StorageType },
): Storage => {
  return new WebStorage({ ...options, storageType: "sessionStorage" });
};
